import React, { PropsWithChildren, useContext, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { accountAccessShowLogin } from 'actions/UiActions'
import { useTranslation, Trans } from 'react-i18next'

import { useAppDispatch } from 'hooks/reduxHooks'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { NavWrapper } from 'components/OfferPage/styles'
import TextButton from 'components/Luxkit/Button/TextButton'
import WalledContent from 'components/Common/WalledContent'
import zIndex from 'styles/tools/z-index'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import GeoContext from 'contexts/geoContext'
import useBedbankRates from 'hooks/useBedbankRates'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import { useHeaderHeightObserver } from 'contexts/HeaderHeightContext'
import { useBusinessTravellerStickyBudgetBarHeightContext } from 'businessTraveller/utils/useBusinessBudgetBarHeightContext'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const HeaderWrapper = styled.div`
  top: 0;
  z-index: ${zIndex.stickyHeader};
  position: sticky;
`

const NavLayoutContainer = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const StyledPackageOptionsButton = styled(TextButton)`
  margin-left: auto;
`

const AuthWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  ${mediaQueryUp.sm}{
    justify-content: flex-end;
  }

  & > * + * {
    margin-left: ${rem(16)};
  }
`

const ButtonsWrapper = styled.div`
  flex-shrink: 0;

  & > * + * {
    margin-left: ${rem(8)};
  }
`

const PriceSummarySection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-shrink: 0;

  ${mediaQueryUp.sm} {
    width: auto;
    padding-left: ${rem(12)};
    justify-content: flex-end;
    margin-left: auto;
  }

  > * + * {
    margin-left: ${rem(12)};
  }
`

const PriceSummary = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;

  ${mediaQueryUp.sm} {
    text-align: right;
  }
`

const DesktopSignUp = styled(TextButton)`
  display: none;

  ${mediaQueryUp.sm}{
    display: inline-flex;
  }
`

const MobileSignUp = styled(TextButton)`
  display: inline-flex;

  ${mediaQueryUp.sm}{
    display: none;
  }
`

interface Props {
  walledGarden: boolean;
  duration: number;
  onViewPackageOptions?: () => void;
  offer: App.BedbankOffer;
  rooms: Array<App.Occupants>;
  checkinDate: moment.Moment;
  checkoutDate: moment.Moment;
}

function BedbankOfferHeader(props: PropsWithChildren<Props>) {
  const {
    children,
    onViewPackageOptions,
    walledGarden,
    offer,
    rooms,
    checkinDate,
    checkoutDate,
    duration,
  } = props
  const { t } = useTranslation(['common, offerPage'])

  const dispatch = useAppDispatch()

  const ref = useRef<HTMLDivElement>(null)
  useHeaderHeightObserver('bedbankOfferHeader', ref)
  const { showTaxExclusivePrices } = useContext(GeoContext)
  const [rates, fetching] = useBedbankRates(offer.id, rooms, checkinDate, checkoutDate)

  const sell = useBedbankOfferSell(offer, duration)

  const hotelPrice = rates[0] ? (showTaxExclusivePrices ? rates[0].totals.exclusive : rates[0].totals.inclusive) : sell?.price ?? 0
  const hotelNights = rates[0]?.nights ?? sell?.los
  const propertyFees = rates[0]?.totals?.propertyFees ?? 0
  const totalPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees

  const datesSelected = !!(checkinDate && checkoutDate)
  const showSummary = !!(hotelNights && hotelPrice)
  const isSoldOut = rates.length == 0 && datesSelected

  const businessTravellerStickyBudgetBarStyle = useBusinessTravellerStickyBudgetBarHeightContext()

  const saleUnit = offer.saleUnit || 'room'

  return (
    <HeaderWrapper style={businessTravellerStickyBudgetBarStyle} ref={ref}>
      <NavWrapper>
        <NavLayoutContainer>
          {children}
          <WalledContent enforced={walledGarden} wall={(
            <AuthWrapper>
              <BodyText variant="medium" weight="semi-bold">{t('Join now to view member prices', { ns: 'common' })}</BodyText>
              <ButtonsWrapper>
                <TextButton kind="secondary" onClick={() => dispatch(accountAccessShowLogin())}>
                  {t('Log In', { ns: 'common' })}
                </TextButton>
                <DesktopSignUp kind="primary" onClick={() => dispatch(accountAccessShowLogin())}>
                  {t('Sign up for free', { ns: 'common' })}
                </DesktopSignUp>
                <MobileSignUp kind="primary" onClick={() => dispatch(accountAccessShowLogin())}>
                  {t('Sign up', { ns: 'common' })}
                </MobileSignUp>
              </ButtonsWrapper>
            </AuthWrapper>
          )}>
            <PriceSummarySection>
              {showSummary && !isSoldOut && !fetching &&
                <PriceSummary>
                  <Caption variant="medium" wrap="no-wrap">{t('nightsFromWithCountFrom', { ns: 'common', count: hotelNights })}</Caption>
                  <div>
                    <Trans i18nKey="pricePerSaleUnit" ns="common" values={{ saleUnit, context: saleUnit, count: 0 }}>
                      <Heading as="span" variant="heading6">
                        <FormatCurrency format="roundedDollar" value={totalPrice} />
                      </Heading>
                      <Caption variant="medium" as="span">/{saleUnit}</Caption>
                    </Trans>
                  </div>
                </PriceSummary>}
              {isSoldOut && !fetching && <UrgencyLabel variant="critical2">{t('SOLD OUT', { ns: 'offerPage' })}</UrgencyLabel>}
              <StyledPackageOptionsButton kind="primary" onClick={onViewPackageOptions}>
                <CSSBreakpoint max="sm">{t('View options', { ns: 'offerPage' })}</CSSBreakpoint>
                <CSSBreakpoint min="md">{t('View package options', { ns: 'offerPage' })}</CSSBreakpoint>
              </StyledPackageOptionsButton>
            </PriceSummarySection>

          </WalledContent>
        </NavLayoutContainer>
      </NavWrapper>
    </HeaderWrapper>
  )
}

export default BedbankOfferHeader
